/**
 * useBlockShipment - Hook para bloquear shipments individuales
 * 
 * Permite bloquear un shipment específico enviando motivo de bloqueo y comentario.
 * Realiza POST a /invoices/shipments/{id}/block con validación de campos requeridos
 * (shipment_block_reason_id y comment). Muestra toasts de éxito/error, cierra el modal
 * y ejecuta callback de refetch al completar exitosamente.
 * 
 * @returns {Object} { blockShipment: función de bloqueo, isBlocking: estado de carga }
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { ShipmentsApiAdapter } from '../adapters/ShipmentsApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';
import { createHttpClient } from '@adapters/HttpClient';

export const useBlockShipment = () => {
  const { t } = useTranslation('invoicesOrdersModule');
  const { toast } = useToast();
  const [isBlocking, setIsBlocking] = useState(false);

  const blockShipment = useCallback(
    async (
      shipmentId: number,
      onSuccess?: () => void,
      data?: { shipment_block_reason_id?: number | string; comment?: string }
    ) => {
      setIsBlocking(true);

      try {
        const shipmentsAdapter = new ShipmentsApiAdapter(
          createHttpClient(apiClientWH)
        );
        await shipmentsAdapter.blockShipment(shipmentId, data);

        // 🎯 Si llegamos aquí, la API devolvió 200 OK = éxito
        toast({
          title: t('toast.block_success.title', 'Shipment bloqueado'),
          description: t('toast.block_success.description', 'El shipment se ha bloqueado exitosamente'),
          variant: 'default',
        });

        // 🎯 Ejecutar callback de éxito (refetch de datos)
        onSuccess?.();
      } catch (error: any) {
        // 🎯 Extraer mensaje amigable de la API
        const apiMessage: string | undefined = error?.response?.data?.message;
        const apiErrors: Record<string, string[] | string> | undefined =
          error?.response?.data?.errors;

        let description = apiMessage;

        // Unir los errores detallados si existen
        if (apiErrors && typeof apiErrors === 'object') {
          const details = Object.values(apiErrors)
            .flat()
            .filter(Boolean)
            .join(' • ');
          description = description ? `${description} — ${details}` : details;
        }

        // Fallback de mensaje
        if (!description) {
          description =
            error?.message ||
            t(
              'toast.block_error.unexpected',
              'Error inesperado al bloquear el shipment. Inténtalo de nuevo.'
            );
        }

        toast({
          title: t('toast.block_error.title', 'Error al bloquear'),
          description,
          variant: 'destructive',
        });
      } finally {
        setIsBlocking(false);
      }
    },
    [t, toast]
  );

  return {
    blockShipment,
    isBlocking,
  };
};

